// src/components/forms/shared/MediaQueryUtils.ts
// Centralized media query system for form components and header nav

import { Theme } from '@mui/material/styles';
import { colors } from '../../../theme/theme';

// Standard breakpoint definitions
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
  xxl: 2560,
} as const;

// Custom breakpoints for your specific use case
export const FORM_BREAKPOINTS = {
  default: 0,
  medium: 1200,
  large: 2560,
} as const;

// StickyMenu specific breakpoints (5+ breakpoints as required)
export const STICKY_MENU_BREAKPOINTS = {
  mobile: 0, // xs
  tablet: 600, // sm
  desktop: 900, // md
  large: 1200, // lg
  mediumLarge: 1400, // no equivalent
  xlarge: 1536, // xl
  xxlarge: 2560, // xxl
} as const;

// Header breakpoints for progressive nav collapse.
// Fixed chrome: Home 64px + bell 64px + profile 64px = 192px.
// Each nav button is 120px (minWidth). Thresholds step evenly by 120px
// from 768px (mobile boundary) as buttons are added left to right.
export const HEADER_BREAKPOINTS = {
  mobile: 768, // below this: hamburger drawer
  showNewClient: 900, // + New Client
  showQuotes: 1020, // + Quotes
  showNewQuote: 1140, // + New Quote
  showKennels: 1260, // + Kennels
  showCalendar: 1380, // + Calendar
  showToday: 1500, // + Today (full nav)
} as const;

// Media query generation utilities
export const createMediaQuery = (
  minWidth: number,
  maxWidth?: number
): string => {
  if (maxWidth) {
    return `@media (min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`;
  }
  return `@media (min-width: ${minWidth}px)`;
};

// Generate media queries for form breakpoints
export const FORM_MEDIA_QUERIES = {
  default: '',
  medium: createMediaQuery(FORM_BREAKPOINTS.medium, FORM_BREAKPOINTS.large - 1),
  large: createMediaQuery(FORM_BREAKPOINTS.large),
} as const;

// NEW: StickyMenu media queries
export const STICKY_MENU_MEDIA_QUERIES = {
  mobile: '',
  tablet: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.tablet,
    STICKY_MENU_BREAKPOINTS.desktop - 1
  ),
  desktop: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.desktop,
    STICKY_MENU_BREAKPOINTS.large - 1
  ),
  large: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.large,
    STICKY_MENU_BREAKPOINTS.mediumLarge - 1
  ),
  mediumLarge: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.mediumLarge,
    STICKY_MENU_BREAKPOINTS.xlarge - 1
  ),
  xlarge: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.xlarge,
    STICKY_MENU_BREAKPOINTS.xxlarge - 1
  ),
  xxlarge: createMediaQuery(STICKY_MENU_BREAKPOINTS.xxlarge),
} as const;

// NEW: FAB media queries - Using same granular breakpoints as StickyMenu
export const FAB_MEDIA_QUERIES = {
  mobile: '',
  tablet: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.tablet,
    STICKY_MENU_BREAKPOINTS.desktop - 1
  ),
  desktop: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.desktop,
    STICKY_MENU_BREAKPOINTS.large - 1
  ),
  large: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.large,
    STICKY_MENU_BREAKPOINTS.mediumLarge - 1
  ),
  mediumLarge: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.mediumLarge,
    STICKY_MENU_BREAKPOINTS.xlarge - 1
  ),
  xlarge: createMediaQuery(
    STICKY_MENU_BREAKPOINTS.xlarge,
    STICKY_MENU_BREAKPOINTS.xxlarge - 1
  ),
  xxlarge: createMediaQuery(STICKY_MENU_BREAKPOINTS.xxlarge),
} as const;

// Breakpoint padding configuration type
export interface BreakpointPadding {
  default?: string;
  medium?: string;
  large?: string;
}

// ENHANCED: StickyMenu styles configuration with all CSS properties
export interface StickyMenuStyles {
  // Positioning
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;

  // Dimensions
  width?: string;
  maxWidth?: string;
  minWidth?: string;
  height?: string;
  maxHeight?: string;
  minHeight?: string;

  // Spacing
  padding?: string;
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  margin?: string;
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;

  // Visual
  borderRadius?: string;
  boxShadow?: string;
  border?: string;
  backgroundColor?: string;

  // Layout
  display?: string;
  flexDirection?: string;
  justifyContent?: string;
  alignItems?: string;
  gap?: string;
}

// Legacy type alias for backward compatibility
export interface StickyMenuPositioning extends StickyMenuStyles {}

// NEW: FAB styles configuration - Extends StickyMenuStyles with FAB-specific properties
export interface FABStyles extends StickyMenuStyles {
  // FAB-specific sizing properties that apply to child elements
  iconSize?: string; // Size for .fab-icon elements (sets fontSize, width, height)
  fontSize?: string; // Size for .fab-text elements
}

export const DEFAULT_BREAKPOINT_PADDING: Required<BreakpointPadding> = {
  default: '0',
  medium: '0',
  large: '0',
} as const;

// Generate padding styles for all breakpoints
export const generateBreakpointPadding = (
  padding: BreakpointPadding = {}
): Record<string, any> => {
  const finalPadding = {
    ...DEFAULT_BREAKPOINT_PADDING,
    ...padding,
  };

  return {
    padding: finalPadding.default,
    [FORM_MEDIA_QUERIES.medium]: {
      padding: finalPadding.medium,
    },
    [FORM_MEDIA_QUERIES.large]: {
      padding: finalPadding.large,
    },
  };
};

// Helper function to create responsive margin styles
export const generateBreakpointMargins = (
  margins: BreakpointPadding = {}
): Record<string, any> => {
  const finalMargins = {
    default: '0',
    medium: '0',
    large: '0',
    ...margins,
  };

  return {
    margin: finalMargins.default,
    [FORM_MEDIA_QUERIES.medium]: {
      margin: finalMargins.medium,
    },
    [FORM_MEDIA_QUERIES.large]: {
      margin: finalMargins.large,
    },
  };
};

// ENHANCED: Generate StickyMenu responsive styles for all CSS properties
export const generateStickyMenuPositioning = (config: {
  mobile?: StickyMenuStyles;
  tablet?: StickyMenuStyles;
  desktop?: StickyMenuStyles;
  large?: StickyMenuStyles;
  mediumLarge?: StickyMenuStyles;
  xlarge?: StickyMenuStyles;
  xxlarge?: StickyMenuStyles;
}): Record<string, any> => {
  const styles: Record<string, any> = {};

  // Helper function to apply styles from a config object
  const applyStyles = (
    target: Record<string, any>,
    source: StickyMenuStyles
  ) => {
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined) {
        target[key] = value;
      }
    });
  };

  // Base mobile styles
  if (config.mobile) {
    applyStyles(styles, config.mobile);
  }

  // Tablet
  if (config.tablet) {
    styles[STICKY_MENU_MEDIA_QUERIES.tablet] = {};
    applyStyles(styles[STICKY_MENU_MEDIA_QUERIES.tablet], config.tablet);
  }

  // Desktop
  if (config.desktop) {
    styles[STICKY_MENU_MEDIA_QUERIES.desktop] = {};
    applyStyles(styles[STICKY_MENU_MEDIA_QUERIES.desktop], config.desktop);
  }

  // Large
  if (config.large) {
    styles[STICKY_MENU_MEDIA_QUERIES.large] = {};
    applyStyles(styles[STICKY_MENU_MEDIA_QUERIES.large], config.large);
  }

  // MediumLarge
  if (config.mediumLarge) {
    styles[STICKY_MENU_MEDIA_QUERIES.mediumLarge] = {};
    applyStyles(
      styles[STICKY_MENU_MEDIA_QUERIES.mediumLarge],
      config.mediumLarge
    );
  }

  // XLarge
  if (config.xlarge) {
    styles[STICKY_MENU_MEDIA_QUERIES.xlarge] = {};
    applyStyles(styles[STICKY_MENU_MEDIA_QUERIES.xlarge], config.xlarge);
  }

  // XXLarge
  if (config.xxlarge) {
    styles[STICKY_MENU_MEDIA_QUERIES.xxlarge] = {};
    applyStyles(styles[STICKY_MENU_MEDIA_QUERIES.xxlarge], config.xxlarge);
  }

  return styles;
};

// NEW: Generate FAB responsive styles for all CSS properties
// Uses granular breakpoints: mobile, tablet, desktop, large, mediumLarge, xlarge, xxlarge
// Maintains consistency with StickyMenu but applies FAB_MEDIA_QUERIES
// SPECIAL HANDLING: iconSize and fontSize are applied to nested .fab-icon and .fab-text selectors
export const generateFABPositioning = (config: {
  mobile?: FABStyles;
  tablet?: FABStyles;
  desktop?: FABStyles;
  large?: FABStyles;
  mediumLarge?: FABStyles;
  xlarge?: FABStyles;
  xxlarge?: FABStyles;
}): Record<string, any> => {
  const styles: Record<string, any> = {};

  // Helper function to apply styles from a config object
  // Handles iconSize and fontSize specially for nested selectors
  const applyStyles = (target: Record<string, any>, source: FABStyles) => {
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined) {
        // Special handling for FAB-specific properties
        if (key === 'iconSize') {
          // Apply iconSize to .fab-icon nested selector
          if (!target['&.custom-styled-fab .fab-icon']) {
            target['&.custom-styled-fab .fab-icon'] = {};
          }
          target['&.custom-styled-fab .fab-icon'].fontSize = value;
          target['&.custom-styled-fab .fab-icon'].width = value;
          target['&.custom-styled-fab .fab-icon'].height = value;

          // Also apply to direct child icons within fab-icon
          if (!target['&.custom-styled-fab .fab-icon > *']) {
            target['&.custom-styled-fab .fab-icon > *'] = {};
          }
          target['&.custom-styled-fab .fab-icon > *'].fontSize =
            `${value} !important`;
          target['&.custom-styled-fab .fab-icon > *'].width =
            `${value} !important`;
          target['&.custom-styled-fab .fab-icon > *'].height =
            `${value} !important`;
        } else if (key === 'fontSize') {
          // Apply fontSize to .fab-text nested selector
          if (!target['&.custom-styled-fab .fab-text']) {
            target['&.custom-styled-fab .fab-text'] = {};
          }
          target['&.custom-styled-fab .fab-text'].fontSize = value;
        } else {
          // Regular CSS properties applied directly
          target[key] = value;
        }
      }
    });
  };

  // Base mobile styles
  if (config.mobile) {
    applyStyles(styles, config.mobile);
  }

  // Tablet
  if (config.tablet) {
    styles[FAB_MEDIA_QUERIES.tablet] = {};
    applyStyles(styles[FAB_MEDIA_QUERIES.tablet], config.tablet);
  }

  // Desktop
  if (config.desktop) {
    styles[FAB_MEDIA_QUERIES.desktop] = {};
    applyStyles(styles[FAB_MEDIA_QUERIES.desktop], config.desktop);
  }

  // Large
  if (config.large) {
    styles[FAB_MEDIA_QUERIES.large] = {};
    applyStyles(styles[FAB_MEDIA_QUERIES.large], config.large);
  }

  // MediumLarge
  if (config.mediumLarge) {
    styles[FAB_MEDIA_QUERIES.mediumLarge] = {};
    applyStyles(styles[FAB_MEDIA_QUERIES.mediumLarge], config.mediumLarge);
  }

  // XLarge
  if (config.xlarge) {
    styles[FAB_MEDIA_QUERIES.xlarge] = {};
    applyStyles(styles[FAB_MEDIA_QUERIES.xlarge], config.xlarge);
  }

  // XXLarge
  if (config.xxlarge) {
    styles[FAB_MEDIA_QUERIES.xxlarge] = {};
    applyStyles(styles[FAB_MEDIA_QUERIES.xxlarge], config.xxlarge);
  }

  return styles;
};

// LEGACY: Static alternating colors (kept for backward compatibility)
// NOTE: This is now deprecated - use generateAlternatingRowStyles with theme parameter instead
export const ALTERNATING_COLORS = {
  even: colors.alternatingRows.lightEven,
  odd: colors.alternatingRows.lightOdd,
  hover: colors.grey[100],
} as const;

// Enhanced alternating colors with custom color support
export interface AlternatingColorConfig {
  even?: string;
  odd?: string;
  hover?: string;
}

export const createAlternatingColors = (
  config: AlternatingColorConfig = {}
): Required<AlternatingColorConfig> => ({
  ...ALTERNATING_COLORS,
  ...config,
});

// Generate theme-aware alternating row styles (colors only)
// Padding is handled separately by ResponsiveFormGrid
export const generateAlternatingRowStyles = (
  enabled: boolean = true,
  customColors: AlternatingColorConfig = {},
  theme?: Theme
) => {
  if (!enabled) return {};

  let finalColors: Required<AlternatingColorConfig>;

  if (theme) {
    const isDarkMode = theme.palette.mode === 'dark';

    const themeColors = isDarkMode
      ? {
          even: theme.palette.alternatingRows.darkEven,
          odd: theme.palette.alternatingRows.darkOdd,
          hover: theme.palette.grey[700],
        }
      : {
          even: theme.palette.alternatingRows.lightEven,
          odd: theme.palette.alternatingRows.lightOdd,
          // hover: theme.palette.grey[100],
          hover: theme.palette.tertiary.main,
        };

    finalColors = {
      ...themeColors,
      ...customColors,
    };
  } else {
    finalColors = createAlternatingColors(customColors);
  }

  return {
    '& > .MuiGrid-item': {
      transition: 'background-color 0.2s ease',

      '&:nth-of-type(even)': {
        backgroundColor: finalColors.even,
      },
      '&:nth-of-type(odd)': {
        backgroundColor: finalColors.odd,
      },
      '&:hover': {
        backgroundColor:
          theme?.palette.mode === 'dark'
            ? theme.palette.primary.dark
            : finalColors.hover,
        '& .displayFieldValue p': {
          color: '#ffffff',
        },
        '& .displayFieldValue div': {
          color: '#ffffff!important',
        },
        '& .displayFieldValue span': {
          color: '#ffffff!important',
        },
        '& .displayFieldValue svg': {
          color: '#ffffff',
        },
      },
    },
  };
};

// Export types for external use
export type MediaQueryBreakpoint = keyof typeof FORM_BREAKPOINTS;
export type FABBreakpoint = keyof typeof FAB_MEDIA_QUERIES;
